import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Code2, Palette, CreditCard, Truck, QrCode, Users, BarChart3 } from "lucide-react";

const sections = [
  {
    icon: BookOpen,
    title: "Guia de Início Rápido",
    desc: "Configure seu restaurante em 5 minutos.",
    steps: [
      "Crie sua conta em /cadastro com e-mail e dados do restaurante.",
      "Acesse o painel e vá em Gerenciar Cardápio para adicionar categorias e produtos.",
      "Em Configurações > QR Codes, gere os códigos para suas mesas.",
      "Compartilhe o link do cardápio digital ou imprima os QR Codes.",
      "Acompanhe os pedidos no Painel da Cozinha em tempo real.",
    ],
  },
  {
    icon: Palette,
    title: "Personalização do Cardápio",
    desc: "Adapte o visual do cardápio à identidade da sua marca.",
    steps: [
      "Acesse Configurações > Aparência para alterar cores, logo e fonte.",
      "Escolha entre layouts: lista, grade ou cards com imagem.",
      "Defina o estilo dos cards: com borda, com sombra ou minimalista.",
      "As alterações são aplicadas em tempo real no cardápio digital.",
    ],
  },
  {
    icon: CreditCard,
    title: "Configuração de Pagamentos",
    desc: "Ative os métodos de pagamento para seus clientes.",
    steps: [
      "Vá em Configurações > Pagamento.",
      "Ative os métodos desejados: PIX, cartão, dinheiro ou MercadoPago.",
      "Para MercadoPago, insira seu Access Token e Public Key da sua conta MP.",
      "Teste um pagamento no cardápio digital para verificar a integração.",
    ],
  },
  {
    icon: Truck,
    title: "Configuração do Delivery",
    desc: "Monte seu delivery próprio sem depender de apps.",
    steps: [
      "Acesse Configurações > Delivery.",
      "Defina o raio de entrega em quilômetros.",
      "Configure a taxa de entrega (fixa ou por distância).",
      "Cadastre seus entregadores com nome, telefone e tipo de veículo.",
      "Defina o valor mínimo do pedido para delivery.",
    ],
  },
  {
    icon: QrCode,
    title: "QR Codes e Mesas",
    desc: "Gere e gerencie os QR Codes do seu restaurante.",
    steps: [
      "Em Configurações > QR Codes, defina a quantidade de mesas.",
      "Clique em 'Gerar QR Codes' para criar os códigos.",
      "Baixe em PDF para impressão ou copie os links individuais.",
      "Cada QR Code direciona para o cardápio com a mesa pré-selecionada.",
    ],
  },
  {
    icon: Users,
    title: "Gestão de Clientes",
    desc: "Conheça e fidelize seus clientes.",
    steps: [
      "Na página Clientes, veja todos os clientes com histórico de pedidos.",
      "Use filtros para segmentar por frequência, valor gasto ou inatividade.",
      "Na aba Reativação, identifique clientes inativos e envie cupons via WhatsApp.",
      "Configure programas de fidelidade em Fidelidade (pontos, selos ou cashback).",
    ],
  },
  {
    icon: BarChart3,
    title: "Relatórios e Analytics",
    desc: "Acompanhe as métricas do seu negócio.",
    steps: [
      "No Dashboard, veja faturamento, pedidos e ticket médio em tempo real.",
      "Em Relatórios, acesse dados detalhados por período.",
      "O Relatório de Custos mostra a margem de lucro de cada produto.",
      "Exporte relatórios em formato compatível para análise externa.",
    ],
  },
  {
    icon: Code2,
    title: "API e Integrações",
    desc: "Conecte o MenuPro ao seu sistema existente.",
    steps: [
      "Acesse a documentação da API em /api para ver os endpoints disponíveis.",
      "Gere sua API Key no painel em Configurações.",
      "Use autenticação Bearer Token em todas as requisições.",
      "Configure webhooks para receber notificações de eventos em tempo real.",
    ],
  },
];

const Documentacao = () => {
  return (
    <div className="min-h-screen bg-background">
      <section className="pt-32 pb-24">
        <div className="container max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" /> Voltar ao início
          </Link>

          <motion.div className="mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Documentação</h1>
            <p className="text-lg text-muted-foreground">
              Guias completos para configurar e aproveitar ao máximo cada recurso da plataforma.
            </p>
          </motion.div>

          {/* TOC */}
          <div className="p-6 rounded-xl bg-gradient-card border border-border mb-12">
            <h2 className="font-bold mb-3">Índice</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {sections.map((s) => (
                <a key={s.title} href={`#${s.title.replace(/\s/g, "-")}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-1">
                  <s.icon className="h-4 w-4" />
                  {s.title}
                </a>
              ))}
            </div>
          </div>

          <div className="space-y-16">
            {sections.map((s) => (
              <motion.section
                key={s.title}
                id={s.title.replace(/\s/g, "-")}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <s.icon className="h-6 w-6 text-primary" />
                  <h2 className="text-2xl font-bold">{s.title}</h2>
                </div>
                <p className="text-muted-foreground mb-5">{s.desc}</p>
                <ol className="space-y-3">
                  {s.steps.map((step, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-xs font-bold">{i + 1}</span>
                      <span className="text-muted-foreground leading-relaxed pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              </motion.section>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Documentacao;
